import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SEO from '../../components/common/SEO';

export default function AntiguaGuide() {
  // Set last-modified meta tag
  useEffect(() => {
    let metaLastModified = document.querySelector('meta[name="last-modified"]');
    if (!metaLastModified) {
      metaLastModified = document.createElement('meta');
      metaLastModified.setAttribute('name', 'last-modified');
      document.head.appendChild(metaLastModified);
    }
    metaLastModified.setAttribute('content', '2026-02-01');

    // Add og:site_name meta tag
    let metaOgSiteName = document.querySelector('meta[property="og:site_name"]');
    if (!metaOgSiteName) {
      metaOgSiteName = document.createElement('meta');
      metaOgSiteName.setAttribute('property', 'og:site_name');
      document.head.appendChild(metaOgSiteName);
    }
    metaOgSiteName.setAttribute('content', 'La Antigua Guatemala');

    return () => {
      const el = document.querySelector('meta[name="last-modified"][content="2026-02-01"]');
      if (el) el.remove();
      const ogSiteName = document.querySelector('meta[property="og:site_name"][content="La Antigua Guatemala"]');
      if (ogSiteName) ogSiteName.remove();
    };
  }, []);

  // Structured Data - Single @graph with all schemas
  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": "https://la-antigua-guatemala.com/antigua-guatemala-travel-guide#webpage",
        "url": "https://la-antigua-guatemala.com/antigua-guatemala-travel-guide",
        "name": "Antigua Guatemala Travel Guide (2026) | Itineraries, Hotels, Day Trips",
        "description": "Complete Antigua Guatemala travel guide (updated 2026) for US travelers: best things to do, where to stay, itineraries, volcano hikes, day trips, safety tips, and airport transfers.",
        "inLanguage": "en-US",
        "isPartOf": {
          "@id": "https://la-antigua-guatemala.com/#website"
        },
        "datePublished": "2025-01-15",
        "dateModified": "2026-02-01"
      },
      {
        "@type": "Article",
        "@id": "https://la-antigua-guatemala.com/antigua-guatemala-travel-guide#article",
        "headline": "Antigua Guatemala Travel Guide (2026) | Itineraries, Hotels, Day Trips",
        "description": "Complete Antigua Guatemala travel guide (updated 2026) for US travelers: best things to do, where to stay, itineraries, volcano hikes, day trips, safety tips, and airport transfers.",
        "url": "https://la-antigua-guatemala.com/antigua-guatemala-travel-guide",
        "datePublished": "2025-01-15",
        "dateModified": "2026-02-01",
        "inLanguage": "en-US",
        "mainEntityOfPage": {
          "@id": "https://la-antigua-guatemala.com/antigua-guatemala-travel-guide#webpage"
        },
        "image": "https://public.readdy.ai/ai/img_res/81fb68b343b847f8c880560bd94743b1.jpg",
        "author": {
          "@type": "Person",
          "name": "Editorial Team"
        },
        "publisher": {
          "@type": "Organization",
          "name": "La Antigua Guatemala",
          "url": "https://la-antigua-guatemala.com/",
          "logo": {
            "@type": "ImageObject",
            "url": "https://static.readdy.ai/image/612c2ae47a27268c7e802411c60f5af5/654eefdeae24ccf8604ad66ae0f6cc69.png"
          }
        },
        "isPartOf": {
          "@id": "https://la-antigua-guatemala.com/#website"
        }
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://la-antigua-guatemala.com/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Antigua Guatemala Travel Guide",
            "item": "https://la-antigua-guatemala.com/antigua-guatemala-travel-guide"
          }
        ]
      }
    ]
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Antigua Guatemala Travel Guide (2026) | Itineraries, Hotels, Day Trips"
        description="Complete Antigua Guatemala travel guide (updated 2026) for US travelers: best things to do, where to stay, itineraries, volcano hikes, day trips, safety tips, and airport transfers."
        canonical="https://la-antigua-guatemala.com/antigua-guatemala-travel-guide"
        type="article"
        lastModified="2026-02-01"
        ogImage={{
          url: "https://public.readdy.ai/ai/img_res/81fb68b343b847f8c880560bd94743b1.jpg",
          width: "1200",
          height: "630"
        }}
        structuredData={structuredData}
      />

      <Navbar darkText />
      
      {/* Hero Section */}
      <section className="relative h-[65vh] md:h-[70vh] flex items-center justify-center mt-20">
        <div className="absolute inset-0">
          <img 
            src="https://readdy.ai/api/search-image?query=Stunning%20aerial%20view%20of%20Antigua%20Guatemala%20colonial%20city%20with%20colorful%20buildings%2C%20cobblestone%20streets%2C%20Agua%20volcano%20in%20background%2C%20vibrant%20colonial%20architecture%2C%20warm%20golden%20hour%20lighting%2C%20travel%20photography%20style&width=1920&height=800&seq=antigua-guide-hero&orientation=landscape"
            alt="Antigua Guatemala colonial architecture"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
        </div>
        <div className="relative z-10 text-center text-white px-6 max-w-4xl mx-auto">
          <h1 className="text-3xl sm:text-4xl md:text-6xl font-bold mb-4 md:mb-6 leading-tight" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.5)' }}>Complete Antigua Guatemala Travel Guide</h1>
          <p className="text-base sm:text-lg md:text-2xl mb-6 md:mb-8" style={{ textShadow: '0 1px 4px rgba(0,0,0,0.4)' }}>Everything US travelers need to plan the perfect trip to Guatemala's colonial gem</p>
          <div className="flex flex-wrap gap-3 md:gap-4 justify-center">
            <Link to="/plan-my-antigua-trip" className="bg-teal-500 hover:bg-teal-600 text-white px-6 py-3 md:px-8 md:py-4 rounded-lg font-semibold transition whitespace-nowrap text-sm md:text-base">
              Plan My Trip
            </Link>
            <Link to="/things-to-do-in-antigua-guatemala" className="bg-white hover:bg-gray-100 text-gray-900 px-6 py-3 md:px-8 md:py-4 rounded-lg font-semibold transition whitespace-nowrap text-sm md:text-base">
              Download Free Itinerary
            </Link>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4">
          {/* Last Updated */}
          <div className="text-center mb-8">
            <p className="text-sm text-gray-500">Last updated: February 2026</p>
          </div>

          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-gray-700 leading-relaxed mb-8">
              Antigua Guatemala is one of Central America's most captivating colonial cities. Nestled in a valley surrounded by three volcanoes, this UNESCO World Heritage site offers cobblestone streets, colorful architecture, world-class coffee, and adventure opportunities that make it a must-visit destination for US travelers. Whether you're planning to visit La Antigua Guatemala for its historic charm or explore Antigua Guatemala, Guatemala as a gateway to volcanic adventures, this comprehensive guide covers everything you need to know.
            </p>

            {/* Table of Contents */}
            <div className="bg-gray-50 border-2 border-gray-200 rounded-lg p-8 mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Table of Contents</h2>
              <div className="grid md:grid-cols-2 gap-3">
                <a href="#quick-facts" className="text-left text-teal-500 hover:text-teal-600 font-medium cursor-pointer">
                  → Quick Facts for US Travelers
                </a>
                <a href="#why-visit" className="text-left text-teal-500 hover:text-teal-600 font-medium cursor-pointer">
                  → Why Visit Antigua Guatemala?
                </a>
                <a href="#how-many-days" className="text-left text-teal-500 hover:text-teal-600 font-medium cursor-pointer">
                  → How Many Days in Antigua?
                </a>
                <a href="#where-to-stay" className="text-left text-teal-500 hover:text-teal-600 font-medium cursor-pointer">
                  → Where to Stay in Antigua
                </a>
                <a href="#getting-there" className="text-left text-teal-500 hover:text-teal-600 font-medium cursor-pointer">
                  → Getting to Antigua from GUA Airport
                </a>
                <a href="#getting-around" className="text-left text-teal-500 hover:text-teal-600 font-medium cursor-pointer">
                  → Getting Around Antigua
                </a>
                <a href="#things-to-do" className="text-left text-teal-500 hover:text-teal-600 font-medium cursor-pointer">
                  → Top Things to Do
                </a>
                <a href="#day-trips" className="text-left text-teal-500 hover:text-teal-600 font-medium cursor-pointer">
                  → Best Day Trips from Antigua
                </a>
                <a href="#volcano-hikes" className="text-left text-teal-500 hover:text-teal-600 font-medium cursor-pointer">
                  → Volcano Hikes
                </a>
                <a href="#safety-tips" className="text-left text-teal-500 hover:text-teal-600 font-medium cursor-pointer">
                  → Safety Tips
                </a>
                <a href="#best-time" className="text-left text-teal-500 hover:text-teal-600 font-medium cursor-pointer">
                  → Best Time to Visit
                </a>
              </div>
            </div>

            {/* Quick Facts for US Travelers */}
            <h2 id="quick-facts" className="text-3xl font-bold text-gray-900 mt-16 mb-6 scroll-mt-28">Quick Facts for US Travelers</h2>
            <div className="bg-white border-2 border-gray-200 rounded-lg p-6 mb-12">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="flex items-start">
                  <div className="w-8 h-8 flex items-center justify-center text-teal-500 mt-1">
                    <i className="ri-money-dollar-circle-line text-xl"></i>
                  </div>
                  <div className="ml-3">
                    <strong className="text-gray-900">Currency:</strong>
                    <span className="text-gray-700 ml-2">Guatemalan Quetzal (GTQ); US dollars widely accepted</span>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-8 h-8 flex items-center justify-center text-teal-500 mt-1">
                    <i className="ri-speak-line text-xl"></i>
                  </div>
                  <div className="ml-3">
                    <strong className="text-gray-900">Language:</strong>
                    <span className="text-gray-700 ml-2">Spanish (English spoken in tourist areas)</span>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-8 h-8 flex items-center justify-center text-teal-500 mt-1">
                    <i className="ri-mountain-line text-xl"></i>
                  </div>
                  <div className="ml-3">
                    <strong className="text-gray-900">Altitude:</strong>
                    <span className="text-gray-700 ml-2">1,500 meters (4,920 feet) — bring layers for cool evenings</span>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-8 h-8 flex items-center justify-center text-teal-500 mt-1">
                    <i className="ri-plug-line text-xl"></i>
                  </div>
                  <div className="ml-3">
                    <strong className="text-gray-900">Plug Type:</strong>
                    <span className="text-gray-700 ml-2">Type A & B (same as US — no adapter needed)</span>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-8 h-8 flex items-center justify-center text-teal-500 mt-1">
                    <i className="ri-sun-line text-xl"></i>
                  </div>
                  <div className="ml-3">
                    <strong className="text-gray-900">Weather:</strong>
                    <span className="text-gray-700 ml-2">Dry season Nov–Apr; rainy season May–Oct. <Link to="/antigua-guatemala-weather" className="text-teal-500 hover:text-teal-600 cursor-pointer">Full weather guide</Link></span>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-8 h-8 flex items-center justify-center text-teal-500 mt-1">
                    <i className="ri-calendar-line text-xl"></i>
                  </div>
                  <div className="ml-3">
                    <strong className="text-gray-900">How Many Days:</strong>
                    <span className="text-gray-700 ml-2">2–5 days recommended (see itineraries below)</span>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-8 h-8 flex items-center justify-center text-teal-500 mt-1">
                    <i className="ri-shield-check-line text-xl"></i>
                  </div>
                  <div className="ml-3">
                    <strong className="text-gray-900">Safety:</strong>
                    <span className="text-gray-700 ml-2">Generally safe in tourist areas with visible police presence. <Link to="/is-antigua-guatemala-safe" className="text-teal-500 hover:text-teal-600 cursor-pointer">Safety guide</Link></span>
                  </div>
                </div>
              </div>
            </div>

            {/* Why Visit */}
            <h2 id="why-visit" className="text-3xl font-bold text-gray-900 mt-16 mb-6 scroll-mt-28">Why Visit Antigua Guatemala?</h2>
            <div className="grid md:grid-cols-2 gap-6 mb-12">
              <div className="bg-gray-50 p-6 rounded-lg">
                <div className="w-12 h-12 flex items-center justify-center text-teal-500 text-3xl mb-4">
                  <i className="ri-building-line"></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Colonial Architecture</h3>
                <p className="text-gray-700">Explore perfectly preserved Spanish colonial buildings, baroque churches, and colorful facades that transport you back in time. The iconic Santa Catalina Arch is just one of many architectural treasures.</p>
              </div>
              <div className="bg-gray-50 p-6 rounded-lg">
                <div className="w-12 h-12 flex items-center justify-center text-teal-500 text-3xl mb-4">
                  <i className="ri-fire-line"></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Volcano Adventures</h3>
                <p className="text-gray-700">Hike active Acatenango for sunrise views of erupting Fuego volcano, or take an easier trek up Pacaya to see active lava flows. The surrounding volcanoes create a dramatic backdrop.</p>
              </div>
              <div className="bg-gray-50 p-6 rounded-lg">
                <div className="w-12 h-12 flex items-center justify-center text-teal-500 text-3xl mb-4">
                  <i className="ri-cup-line"></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Coffee Culture</h3>
                <p className="text-gray-700">Tour world-renowned coffee plantations and taste some of the finest beans grown in Guatemala's highlands. Learn about the entire coffee-making process from bean to cup.</p>
              </div>
              <div className="bg-gray-50 p-6 rounded-lg">
                <div className="w-12 h-12 flex items-center justify-center text-teal-500 text-3xl mb-4">
                  <i className="ri-map-pin-line"></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Perfect Location</h3>
                <p className="text-gray-700">Just 45 minutes from Guatemala City airport, Antigua is the ideal base for exploring Guatemala's highlights including Lake Atitlán, Chichicastenango market, and Mayan ruins.</p>
              </div>
              <div className="bg-gray-50 p-6 rounded-lg">
                <div className="w-12 h-12 flex items-center justify-center text-teal-500 text-3xl mb-4">
                  <i className="ri-global-line"></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">UNESCO World Heritage</h3>
                <p className="text-gray-700">Recognized since 1979 for its exceptional preservation of Spanish colonial architecture and urban planning, Antigua offers an authentic glimpse into colonial Latin America.</p>
              </div>
              <div className="bg-gray-50 p-6 rounded-lg">
                <div className="w-12 h-12 flex items-center justify-center text-teal-500 text-3xl mb-4">
                  <i className="ri-restaurant-line"></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Culinary Scene</h3>
                <p className="text-gray-700">From traditional Guatemalan cuisine to international fine dining, Antigua's restaurant scene rivals any major city, with rooftop bars offering stunning volcano views.</p>
              </div>
            </div>

            {/* How Many Days */}
            <h2 id="how-many-days" className="text-3xl font-bold text-gray-900 mt-16 mb-6 scroll-mt-28">How Many Days in Antigua?</h2>
            <p className="text-gray-700 mb-6">
              The ideal length of stay depends on your interests and travel style. Here's a breakdown to help you decide:
            </p>
            <div className="space-y-4 mb-12">
              <Link to="/antigua-guatemala-2-week-itinerary" className="block bg-white border-2 border-gray-200 hover:border-teal-500 p-6 rounded-lg transition cursor-pointer">
                <div className="flex items-start">
                  <div className="w-12 h-12 flex items-center justify-center text-teal-500 text-2xl mr-4">
                    <i className="ri-time-line"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">2 Days in Antigua</h3>
                    <p className="text-gray-600">Perfect for a quick weekend getaway. Explore the colonial center, visit key landmarks, and complete one volcano hike (Pacaya recommended for time efficiency).</p>
                  </div>
                </div>
              </Link>
              <Link to="/antigua-guatemala-3-day-itinerary" className="block bg-white border-2 border-gray-200 hover:border-teal-500 p-6 rounded-lg transition cursor-pointer">
                <div className="flex items-start">
                  <div className="w-12 h-12 flex items-center justify-center text-teal-500 text-2xl mr-4">
                    <i className="ri-calendar-check-line"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">3 Days in Antigua (Recommended)</h3>
                    <p className="text-gray-600">Ideal for first-time visitors. Includes city exploration, Acatenango overnight hike, and a cultural day trip to Chichicastenango or Lake Atitlán.</p>
                  </div>
                </div>
              </Link>
              <Link to="/antigua-guatemala-2-week-itinerary" className="block bg-white border-2 border-gray-200 hover:border-teal-500 p-6 rounded-lg transition cursor-pointer">
                <div className="flex items-start">
                  <div className="w-12 h-12 flex items-center justify-center text-teal-500 text-2xl mr-4">
                    <i className="ri-calendar-2-line"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">5 Days in Antigua</h3>
                    <p className="text-gray-700">Comprehensive experience with time for multiple day trips, both volcano hikes, coffee plantation tours, Spanish lessons, and deeper neighborhood exploration.</p>
                  </div>
                </div>
              </Link>
            </div>

            {/* Where to Stay */}
            <h2 id="where-to-stay" className="text-3xl font-bold text-gray-900 mt-16 mb-6 scroll-mt-28">Where to Stay in Antigua</h2>
            <p className="text-gray-700 mb-6">
              Antigua's compact size makes most areas walkable, but staying near Parque Central puts you in the heart of the action. The best neighborhoods for tourists include:
            </p>
            <div className="bg-gray-50 p-6 rounded-lg mb-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Best Areas to Stay:</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <div className="w-6 h-6 flex items-center justify-center text-teal-500 mt-1">
                    <i className="ri-map-pin-fill"></i>
                  </div>
                  <div className="ml-3">
                    <strong className="text-gray-900">Near Parque Central:</strong>
                    <span className="text-gray-700 ml-2">Walking distance to restaurants, shops, and attractions. Can be noisier but most convenient.</span>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="w-6 h-6 flex items-center justify-center text-teal-500 mt-1">
                    <i className="ri-map-pin-fill"></i>
                  </div>
                  <div className="ml-3">
                    <strong className="text-gray-900">North of the Arch:</strong>
                    <span className="text-gray-700 ml-2">Quieter residential area, still walkable to center. Great for boutique hotels and peaceful stays.</span>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="w-6 h-6 flex items-center justify-center text-teal-500 mt-1">
                    <i className="ri-map-pin-fill"></i>
                  </div>
                  <div className="ml-3">
                    <strong className="text-gray-900">South of Parque Central:</strong>
                    <span className="text-gray-700 ml-2">Mix of hotels and local life. Easy access to markets and slightly lower prices.</span>
                  </div>
                </li>
              </ul>
            </div>
            <p className="text-gray-700 mb-6">
              Accommodation options range from luxury colonial mansions converted into boutique hotels to budget-friendly hostels with social atmospheres. Most hotels feature beautiful courtyards, rooftop terraces with volcano views, and personalized service.
            </p>
            <Link to="/best-hotels-in-antigua-guatemala" className="inline-block bg-teal-500 hover:bg-teal-600 text-white px-6 py-3 rounded-lg font-semibold transition whitespace-nowrap mb-12">
              See Best Hotels & Where to Stay Guide
            </Link>

            {/* Getting There */}
            <h2 id="getting-there" className="text-3xl font-bold text-gray-900 mt-16 mb-6 scroll-mt-28">Getting to Antigua from Guatemala City Airport (GUA)</h2>
            <p className="text-gray-700 mb-6">
              Most US travelers fly into Guatemala City's La Aurora International Airport (GUA). From there, Antigua is approximately 45 kilometers (28 miles) away, taking about 1 hour by road depending on traffic.
            </p>
            <div className="bg-white border-2 border-gray-200 rounded-lg p-6 mb-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Transportation Options:</h3>
              <div className="space-y-4">
                <div>
                  <strong className="text-gray-900">Shared Shuttle (Recommended):</strong>
                  <p className="text-gray-700 mt-1">Most popular option for budget-conscious travelers. Shuttles depart regularly throughout the day and cost around $10–15 per person. Book in advance for guaranteed seats.</p>
                </div>
                <div>
                  <strong className="text-gray-900">Private Transfer:</strong>
                  <p className="text-gray-700 mt-1">Best for families or groups. More expensive ($40–60) but offers door-to-door service, flexibility with timing, and no waiting for other passengers.</p>
                </div>
                <div>
                  <strong className="text-gray-900">Uber/Taxi:</strong>
                  <p className="text-gray-700 mt-1">Available but not recommended due to safety concerns. If you must use a taxi, only use registered airport taxis and agree on the price beforehand.</p>
                </div>
              </div>
            </div>
            <p className="text-gray-700 mb-6">
              <strong>Safety tip:</strong> We strongly recommend booking your airport transfer in advance through a reputable company. This ensures safe, reliable transportation and eliminates the stress of negotiating with taxi drivers upon arrival.
            </p>
            <Link to="/guatemala-city-airport-to-antigua" className="inline-block bg-teal-500 hover:bg-teal-600 text-white px-6 py-3 rounded-lg font-semibold transition whitespace-nowrap mb-12">
              Complete Airport Transfer Guide
            </Link>

            {/* Getting Around */}
            <h2 id="getting-around" className="text-3xl font-bold text-gray-900 mt-16 mb-6 scroll-mt-28">Getting Around Antigua</h2>
            <p className="text-gray-700 mb-6">
              Antigua's compact colonial center is highly walkable, and most attractions are within a 15-minute walk of Parque Central. However, the cobblestone streets can be uneven, so wear comfortable walking shoes.
            </p>
            <div className="grid md:grid-cols-3 gap-6 mb-6">
              <div className="bg-gray-50 p-6 rounded-lg">
                <div className="w-12 h-12 flex items-center justify-center text-teal-500 text-3xl mb-3">
                  <i className="ri-walk-line"></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Walking</h3>
                <p className="text-gray-700">Best way to explore the colonial center. Watch for uneven cobblestones and bring comfortable shoes.</p>
              </div>
              <div className="bg-gray-50 p-6 rounded-lg">
                <div className="w-12 h-12 flex items-center justify-center text-teal-500 text-3xl mb-3">
                  <i className="ri-taxi-line"></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Tuk-Tuks</h3>
                <p className="text-gray-700">Convenient for short trips or when tired. Typical fare is Q10–20 ($1.30–2.60) within the city center.</p>
              </div>
              <div className="bg-gray-50 p-6 rounded-lg">
                <div className="w-12 h-12 flex items-center justify-center text-teal-500 text-3xl mb-3">
                  <i className="ri-car-line"></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Taxis</h3>
                <p className="text-gray-700">Available for longer trips or late-night travel. Always agree on the price before getting in.</p>
              </div>
            </div>
            <p className="text-gray-700 mb-12">
              <strong>Pro tip:</strong> Download the offline map of Antigua on Google Maps before your trip. Cell service can be spotty in some areas, and having an offline map ensures you won't get lost navigating the colonial streets.
            </p>

            {/* Things to Do */}
            <h2 id="things-to-do" className="text-3xl font-bold text-gray-900 mt-16 mb-6 scroll-mt-28">Top Things to Do in Antigua Guatemala</h2>
            <p className="text-gray-700 mb-6">
              From colonial architecture to volcano hikes, Antigua offers diverse experiences for every type of traveler. Here are the must-do activities:
            </p>
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className="bg-white border-2 border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Santa Catalina Arch</h3>
                <p className="text-gray-700">Antigua's most iconic landmark, this yellow arch frames Agua volcano perfectly. Best photographed in early morning or late afternoon.</p>
              </div>
              <div className="bg-white border-2 border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Cerro de la Cruz Viewpoint</h3>
                <p className="text-gray-700">Hike or tuk-tuk to this hilltop viewpoint for panoramic views of the city and surrounding volcanoes. Go with a group for safety.</p>
              </div>
              <div className="bg-white border-2 border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Colonial Ruins & Churches</h3>
                <p className="text-gray-700">Explore earthquake-damaged ruins like Convento de las Capuchinas and visit active churches including La Merced with its ornate yellow facade.</p>
              </div>
              <div className="bg-white border-2 border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Coffee Farm Tours</h3>
                <p className="text-gray-700">Visit working coffee plantations to learn about cultivation, processing, and roasting. Many tours include tastings of Guatemala's famous beans.</p>
              </div>
              <div className="bg-white border-2 border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Local Markets & Crafts</h3>
                <p className="text-gray-700">Browse colorful textiles, jade jewelry, and handicrafts at the daily artisan market near Parque Central and the larger Mercado de Artesanías.</p>
              </div>
              <div className="bg-white border-2 border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Rooftop Sunsets</h3>
                <p className="text-gray-700">Watch the sun set behind the volcanoes from one of Antigua's many rooftop bars and restaurants. Café Sky is a popular choice.</p>
              </div>
            </div>
            <Link to="/things-to-do-in-antigua-guatemala" className="inline-block text-teal-500 hover:text-teal-600 font-semibold text-lg cursor-pointer mb-12">
              See Complete Things to Do Guide →
            </Link>

            {/* Day Trips */}
            <h2 id="day-trips" className="text-3xl font-bold text-gray-900 mt-16 mb-6 scroll-mt-28">Best Day Trips from Antigua</h2>
            <p className="text-gray-700 mb-6">
              Antigua's central location makes it the perfect base for exploring Guatemala's most famous attractions. Popular day trips include:
            </p>
            <div className="space-y-4 mb-6">
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Lake Atitlán</h3>
                <p className="text-gray-700">Often called the most beautiful lake in the world, Lake Atitlán is surrounded by volcanoes and indigenous villages. Full-day tours typically visit 2–3 lakeside towns.</p>
              </div>
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Chichicastenango Market</h3>
                <p className="text-gray-700">Guatemala's largest indigenous market operates on Thursdays and Sundays. Shop for textiles, masks, and handicrafts while experiencing authentic Mayan culture.</p>
              </div>
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Pacaya Volcano</h3>
                <p className="text-gray-700">Easier than Acatenango, this half-day hike takes you to active lava flows where you can roast marshmallows over volcanic heat.</p>
              </div>
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Iximché Mayan Ruins</h3>
                <p className="text-gray-700">Pre-Columbian archaeological site with well-preserved pyramids and plazas. Less crowded than Tikal and closer to Antigua.</p>
              </div>
            </div>
            <Link to="/day-trips-from-antigua-guatemala" className="inline-block bg-teal-500 hover:bg-teal-600 text-white px-6 py-3 rounded-lg font-semibold transition whitespace-nowrap mb-12">
              Explore All Day Trip Options
            </Link>

            {/* Volcano Hikes */}
            <h2 id="volcano-hikes" className="text-3xl font-bold text-gray-900 mt-16 mb-6 scroll-mt-28">Volcano Hikes from Antigua</h2>
            <p className="text-gray-700 mb-6">
              Hiking one of the surrounding volcanoes is a highlight of any Antigua visit. The two most popular options offer very different experiences:
            </p>
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <div className="bg-white border-2 border-gray-200 rounded-lg overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=Acatenango%20volcano%20hike%20camping%20tents%20with%20view%20of%20erupting%20Fuego%20volcano%20at%20sunset%2C%20dramatic%20volcanic%20landscape%2C%20adventure%20travel%20photography%2C%20warm%20orange%20and%20red%20tones%20from%20lava&width=600&height=400&seq=acatenango-card&orientation=landscape"
                  alt="Acatenango volcano hike"
                  className="w-full h-64 object-cover object-center"
                />
                <div className="p-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Acatenango Overnight Hike</h3>
                  <p className="text-gray-700 mb-4">
                    <strong>Difficulty:</strong> Challenging<br/>
                    <strong>Duration:</strong> 2 days / 1 night<br/>
                    <strong>Elevation:</strong> 3,976 meters (13,045 feet)
                  </p>
                  <p className="text-gray-700 mb-4">
                    Camp on the volcano and watch Fuego erupt throughout the night. This challenging hike rewards you with unforgettable sunrise views and volcanic fireworks. Requires good fitness level.
                  </p>
                  <Link to="/acatenango-hike-from-antigua" className="inline-block text-teal-500 hover:text-teal-600 font-semibold cursor-pointer">
                    Complete Acatenango Guide →
                  </Link>
                </div>
              </div>

              <div className="bg-white border-2 border-gray-200 rounded-lg overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=Pacaya%20volcano%20active%20lava%20flows%20glowing%20red%2C%20hikers%20roasting%20marshmallows%20over%20volcanic%20heat%2C%20dramatic%20volcanic%20landscape%2C%20adventure%20travel%20photography%2C%20warm%20lighting&width=600&height=400&seq=pacaya-card&orientation=landscape"
                  alt="Pacaya volcano hike"
                  className="w-full h-64 object-cover object-center"
                />
                <div className="p-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Pacaya Volcano Day Hike</h3>
                  <p className="text-gray-700 mb-4">
                    <strong>Difficulty:</strong> Moderate<br/>
                    <strong>Duration:</strong> Half day (4–5 hours total)<br/>
                    <strong>Elevation:</strong> 2,552 meters (8,373 feet)
                  </p>
                  <p className="text-gray-700 mb-4">
                    Easier and shorter than Acatenango, Pacaya offers close-up views of active lava flows. Perfect for families or those with limited time. Roast marshmallows over volcanic heat!
                  </p>
                  <Link to="/pacaya-volcano-day-trip-from-antigua" className="inline-block text-teal-500 hover:text-teal-600 font-semibold cursor-pointer">
                    Complete Pacaya Guide →
                  </Link>
                </div>
              </div>
            </div>

            {/* Safety Tips */}
            <h2 id="safety-tips" className="text-3xl font-bold text-gray-900 mt-16 mb-6 scroll-mt-28">Safety Tips for Antigua Guatemala</h2>
            <p className="text-gray-700 mb-6">
              Antigua is generally safe for tourists, especially in the well-traveled colonial center where there's a visible police presence. However, like any tourist destination, it's important to take common-sense precautions:
            </p>
            <div className="bg-white border-2 border-gray-200 rounded-lg p-6 mb-6">
              <ul className="space-y-3">
                <li className="flex items-start">
                  <div className="w-6 h-6 flex items-center justify-center text-teal-500 mt-1">
                    <i className="ri-check-line text-xl"></i>
                  </div>
                  <p className="ml-3 text-gray-700"><strong>Stay in well-lit areas at night</strong> and avoid walking alone after dark, especially in less touristy neighborhoods.</p>
                </li>
                <li className="flex items-start">
                  <div className="w-6 h-6 flex items-center justify-center text-teal-500 mt-1">
                    <i className="ri-check-line text-xl"></i>
                  </div>
                  <p className="ml-3 text-gray-700"><strong>Use registered transportation</strong> — book shuttles and tours through reputable companies, not street vendors.</p>
                </li>
                <li className="flex items-start">
                  <div className="w-6 h-6 flex items-center justify-center text-teal-500 mt-1">
                    <i className="ri-check-line text-xl"></i>
                  </div>
                  <p className="ml-3 text-gray-700"><strong>Keep valuables secure</strong> — don't flash expensive jewelry, cameras, or large amounts of cash.</p>
                </li>
                <li className="flex items-start">
                  <div className="w-6 h-6 flex items-center justify-center text-teal-500 mt-1">
                    <i className="ri-check-line text-xl"></i>
                  </div>
                  <p className="ml-3 text-gray-700"><strong>Hike volcanoes with organized tours</strong> — never hike alone, and always go with a licensed guide.</p>
                </li>
                <li className="flex items-start">
                  <div className="w-6 h-6 flex items-center justify-center text-teal-500 mt-1">
                    <i className="ri-check-line text-xl"></i>
                  </div>
                  <p className="ml-3 text-gray-700"><strong>Be cautious at ATMs</strong> — use machines inside banks during business hours when possible.</p>
                </li>
                <li className="flex items-start">
                  <div className="w-6 h-6 flex items-center justify-center text-teal-500 mt-1">
                    <i className="ri-check-line text-xl"></i>
                  </div>
                  <p className="ml-3 text-gray-700"><strong>Watch your drinks</strong> — never leave drinks unattended in bars or restaurants.</p>
                </li>
              </ul>
            </div>
            <p className="text-gray-700 mb-6">
              Most visitors to Antigua have safe, incident-free trips by following these basic precautions. The tourist police (Politur) patrol the main areas regularly and are helpful if you need assistance.
            </p>
            <Link to="/is-antigua-guatemala-safe" className="inline-block text-teal-500 hover:text-teal-600 font-semibold text-lg cursor-pointer mb-12">
              Read Complete Safety Guide →
            </Link>

            {/* Best Time to Visit */}
            <h2 id="best-time" className="text-3xl font-bold text-gray-900 mt-16 mb-6 scroll-mt-28">Best Time to Visit Antigua Guatemala</h2>
            <p className="text-gray-700 mb-6">
              Antigua can be visited year-round, but the dry season offers the most reliable weather for outdoor activities and volcano hikes.
            </p>
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Dry Season (November–April)</h3>
                <p className="text-gray-700 mb-3"><strong>Best for:</strong> Volcano hikes, outdoor activities, clear views</p>
                <p className="text-gray-700">
                  Peak tourist season with sunny days and cool nights. December through March sees the most visitors and highest prices. Book accommodations and tours in advance, especially around Christmas and Semana Santa (Holy Week in March/April).
                </p>
              </div>
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Rainy Season (May–October)</h3>
                <p className="text-gray-700 mb-3"><strong>Best for:</strong> Budget travelers, fewer crowds, lush landscapes</p>
                <p className="text-gray-700">
                  Mornings are typically clear with afternoon/evening showers. Lower prices and fewer tourists make this an attractive option. Volcano hikes are still possible but views may be obscured by clouds. September and October see the heaviest rainfall.
                </p>
              </div>
            </div>
            <div className="bg-teal-50 border-2 border-teal-200 rounded-lg p-6 mb-12">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Special Events:</h3>
              <p className="text-gray-700 mb-2">
                <strong>Semana Santa (Holy Week):</strong> Antigua's most spectacular event with elaborate alfombras (sawdust carpets) and religious processions. Extremely crowded — book months in advance.
              </p>
              <p className="text-gray-700">
                <strong>Day of the Dead (November 1):</strong> Traditional celebrations with colorful kites and cemetery visits. Less crowded than Semana Santa but still festive.
              </p>
            </div>
            <Link to="/best-time-to-visit-antigua-guatemala" className="inline-block text-teal-500 hover:text-teal-600 font-semibold text-lg cursor-pointer">
              Month-by-Month Weather Breakdown →
            </Link>
          </div>
        </div>
      </section>

      {/* Quick Navigation */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Quick Links to Essential Guides</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Link to="/things-to-do-in-antigua-guatemala" className="bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition text-center group cursor-pointer">
              <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center text-teal-500 text-3xl">
                <i className="ri-map-pin-line"></i>
              </div>
              <h3 className="font-semibold text-gray-900 group-hover:text-teal-500 transition">Things To Do</h3>
            </Link>
            <Link to="/best-hotels-in-antigua-guatemala" className="bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition text-center group cursor-pointer">
              <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center text-teal-500 text-3xl">
                <i className="ri-hotel-line"></i>
              </div>
              <h3 className="font-semibold text-gray-900 group-hover:text-teal-500 transition">Where To Stay</h3>
            </Link>
            <Link to="/day-trips-from-antigua-guatemala" className="bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition text-center group cursor-pointer">
              <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center text-teal-500 text-3xl">
                <i className="ri-compass-line"></i>
              </div>
              <h3 className="font-semibold text-gray-900 group-hover:text-teal-500 transition">Day Trips</h3>
            </Link>
            <Link to="/acatenango-hike-from-antigua" className="bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition text-center group cursor-pointer">
              <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center text-teal-500 text-3xl">
                <i className="ri-fire-line"></i>
              </div>
              <h3 className="font-semibold text-gray-900 group-hover:text-teal-500 transition">Volcano Hikes</h3>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-teal-500">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">Ready To Plan Your Antigua Adventure?</h2>
          <p className="text-xl text-white/90 mb-8">Get personalized recommendations for shuttles, tours, and hotels based on your travel dates and preferences.</p>
          <Link to="/plan-my-antigua-trip" className="inline-block bg-white hover:bg-gray-100 text-teal-500 px-8 py-4 rounded-lg font-semibold text-lg transition whitespace-nowrap">
            Get Custom Recommendations
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}